import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DataService } from '../data-service';
import { URLHelper } from 'helpers/url.helper';
import {ApplyCoupon, BillType, Discount, DiscountType, ParkingLotUsage, ParkingLotUsageQuery} from 'helpers/interfaces';

@Injectable()
export class PriviledgeDataService extends DataService {

  /* Promotion */

  getPromotions(options: any, searchFilter: string, searchCategory?: string) {
    options = options || {};
    options.query = options.query || {};
    options.query['query'] = URLHelper.buildGeneralSearchFilter(
      {'name': 'string'},
      searchFilter || '*');
    let url: string = URLHelper.buildQueryString('/coupons/search?', options.query, '');
    if (searchCategory) url = url + `;(status==${searchCategory})`;
    return this.get("", {"parking-lots": "PARKING_LOT_ID"}, url + '&sort=createdDate,desc');
  }

  getPromotionQRCode(couponId: any): Promise<any> {
    let options = {observe: 'response', responseType: "blob"};
    return this.post("", {"parking-lots": "PARKING_LOT_ID", "coupons": couponId}, "/image", {}, options);
  }

  addPromotion(data: any): Promise<any> {
    let options = { headers: new HttpHeaders().set('Content-Type', 'application/json') };
    return this.post("", {"parking-lots": "PARKING_LOT_ID"}, "/coupons", data, options);
  }

  updatePromotion(couponId: number, data: any): Promise<any> {
    let options = { headers: new HttpHeaders().set('Content-Type', 'application/json') };
    return this.put("", {"parking-lots": "PARKING_LOT_ID", "coupons": couponId}, '', data, options);
  }

  removePromotion(couponId: Array<any>): Promise<any> {
    let couponIdsFormat = couponId.map((data) => {
      return data.id;
    }).join(",");
    return this.delete("", {"parking-lots": "PARKING_LOT_ID"}, "/coupons?ids=" + couponIdsFormat);
  }

  /* Coupon */
  searchParkingUsage(queryParams: ParkingLotUsageQuery): Promise<ParkingLotUsage> {
    let url: string = '/usages/search?'
    if (queryParams.licensePlateLetter && queryParams.licensePlateNumber && queryParams.licensePlateProvince)
      url += `query=(licensePlateLetter=="${queryParams.licensePlateLetter}";licensePlateNumber=="${queryParams.licensePlateNumber}";licensePlateProvince=="${queryParams.licensePlateProvince}")`;
    else if (queryParams.phoneNumber)
      url += `query=(phoneNumber==${queryParams.phoneNumber})`;
    else if (queryParams.slipNumber)
      url += `query=(slipNumber==${queryParams.slipNumber})`;

    return this.get("", {"parking-lots": "PARKING_LOT_ID"}, url + `;(usageType==GATE)&sort=createdDate,desc`);
  }

  advancedSearchParkingUsage(queryParams: ParkingLotUsageQuery, limit: number = 10, offset: number = 0): Promise<ParkingLotUsage> {
    let url: string = '/usages/search?'
    if (queryParams.licensePlateLetter && queryParams.licensePlateNumber && queryParams.licensePlateProvince)
      url += `query=(licensePlateLetter=='${queryParams.licensePlateLetter}';licensePlateNumber=='${queryParams.licensePlateNumber}';licensePlateProvince=='${queryParams.licensePlateProvince}')`;
    else if (!queryParams.licensePlateLetter && queryParams.licensePlateNumber && queryParams.licensePlateProvince)
      url += `query=(licensePlateNumber=='${queryParams.licensePlateNumber}';licensePlateProvince=='${queryParams.licensePlateProvince}')`;
    else if (queryParams.licensePlateLetter && !queryParams.licensePlateNumber && queryParams.licensePlateProvince)
      url += `query=(licensePlateLetter=='${queryParams.licensePlateLetter}';licensePlateProvince=='${queryParams.licensePlateProvince}')`;
    else if (queryParams.licensePlateLetter && queryParams.licensePlateNumber && !queryParams.licensePlateProvince)
      url += `query=(licensePlateLetter=='${queryParams.licensePlateLetter}';licensePlateNumber=='${queryParams.licensePlateNumber}')`;
    else if (queryParams.licensePlateLetter && !queryParams.licensePlateNumber && !queryParams.licensePlateProvince)
      url += `query=(licensePlateLetter=='${queryParams.licensePlateLetter}')`;
    else if (!queryParams.licensePlateLetter && queryParams.licensePlateNumber && !queryParams.licensePlateProvince)
      url += `query=(licensePlateNumber=='${queryParams.licensePlateNumber}')`;
    else if (!queryParams.licensePlateLetter && !queryParams.licensePlateNumber && queryParams.licensePlateProvince)
      url += `query=(licensePlateProvince=='${queryParams.licensePlateProvince}')`;
    
    let sortParam = 'sort=checkedIn,desc';
    let paginationParam = `offset=${offset}&limit=${limit}`;

    return this.get("", 
      {"parking-lots": "PARKING_LOT_ID"}, 
      url + `;(usageType==GATE);(usageStatus==PARKED)&searchOption=ADVANCE&${sortParam}&${paginationParam}`
    );
  }

  applyCoupon(parkingLotUsageId: number, amount: number, type: DiscountType, billFee?: number, billType?: BillType): Promise<Discount> {
    const options = { headers: new HttpHeaders().set('Content-Type', 'application/json') };
    let payload: ApplyCoupon = { amount: amount, type: type };

    if (billFee && billType) {
      payload.billFee = billFee;
      payload.billType = billType;
    }

    if (DiscountType.HOUR === type && amount === -1) {
      payload.amount = 0;
      payload.type = DiscountType.FREE;
    }

    return this.post("", {"parking-lots": "PARKING_LOT_ID", "usages": parkingLotUsageId}, '/coupons/apply', payload, options);
  }

  calculateDiscountFromBill(billFee: number, billType: BillType): Promise<ApplyCoupon> {
    const uri = URLHelper.buildQueryString("/calculate-discount-from-bill?", { billFee: billFee,  billType: billType}, '')
    return this.get("", {"parking-lots": "PARKING_LOT_ID"}, uri);
  }

  searchCoupon(province: string, plateLetter: string, plateNumber: string) {
    let query = { licensePlateLetter: plateLetter,
                  licensePlateNumber: plateNumber,
                  licensePlateProvince: province
                };
    return this.get("", {"parking-lots": "PARKING_LOT_ID"},
                    URLHelper.buildQueryString("/vehicles/currently-usages?", query, ''));
  }

  getAppliedCoupon(usageId: number) {
    return this.get("", {"parking-lots": "PARKING_LOT_ID", "usages": usageId}, "/discounts");
  }

  getCarImage(parkingLotUsageId: number) {
    return this.get("", {}, 'files?refId=' + parkingLotUsageId + '&parkingLotId=PARKING_LOT_ID' + '&purpose=PARKING_LOT_USAGE_CHECKIN_LICENSE_PLATE');
  }
}



// WEBPACK FOOTER //
// ./src/app/core/data-services/priviledge/priviledge.service.ts